import ethIcon from 'cryptocurrency-icons/svg/color/eth.svg';
import bnbIcon from 'cryptocurrency-icons/svg/color/bnb.svg';
import usdtIcon from 'cryptocurrency-icons/svg/color/usdt.svg';
import usdcIcon from 'cryptocurrency-icons/svg/color/usdc.svg';
import baseIcon from '../assets/icons/base.svg';
import {
  getSupportedAssets as getSdkAssets,
  getAssetById as getSdkAssetById,
  getDefaultAsset as getSdkDefaultAsset,
  getSupportedNetworks as getSdkNetworks,
} from '@tokenbox/wallet-kit';

const ICON_MAP = {
  eth: ethIcon,
  base: baseIcon,
  bsc: bnbIcon,
  usdt: usdtIcon,
  usdc: usdcIcon,
};

const DEFAULT_ASSET_FILTER_OPTIONS = {
  includeLocal: false,
  includeMainnets: true,
  includeTestnets: true,
};

const DEFAULT_NETWORK_FILTER_OPTIONS = {
  includeLocal: false,
  includeMainnets: true,
  includeTestnets: true,
};

function withUiMetadata(asset) {
  const contractAddresses = asset.networks.reduce((acc, network) => {
    if (network.contractAddress) {
      acc[network.networkId] = network.contractAddress;
    }
    return acc;
  }, {});

  const firstContractAddress = Object.values(contractAddresses)[0] || null;

  return {
    ...asset,
    assetId: asset.id,
    icon: ICON_MAP[asset.id] || null,
    contractAddress: firstContractAddress,
    contractAddresses: Object.keys(contractAddresses).length > 0 ? contractAddresses : undefined,
  };
}

export function getSupportedCurrencies(options = {}) {
  const assets = getSdkAssets({ ...DEFAULT_ASSET_FILTER_OPTIONS, ...options });
  return assets.map(withUiMetadata);
}

export const SUPPORTED_CURRENCIES = getSupportedCurrencies();

export function getCurrencyById(currencyId, options = {}) {
  if (!currencyId) return null;
  const asset = getSdkAssetById(currencyId, { ...DEFAULT_ASSET_FILTER_OPTIONS, ...options });
  return asset ? withUiMetadata(asset) : null;
}

export function isCurrencySupported(currencyId, options = {}) {
  return !!getCurrencyById(currencyId, options);
}

export function getDefaultCurrency(options = {}) {
  const asset = getSdkDefaultAsset({ ...DEFAULT_ASSET_FILTER_OPTIONS, ...options });
  return asset ? withUiMetadata(asset) : null;
}

export function getSupportedNetworkConfigs(options = {}) {
  return getSdkNetworks({ ...DEFAULT_NETWORK_FILTER_OPTIONS, ...options });
}

export function getNetworkConfig(networkId, options = {}) {
  if (!networkId) return null;
  const networks = getSupportedNetworkConfigs(options);
  return networks.find((network) => network.id === networkId) || null;
}

export function getExplorerUrl(networkId, options = {}) {
  const network = getNetworkConfig(networkId, options);
  return network?.explorerUrl || 'https://sepolia.basescan.org';
}

/**
 * Get the fixed USDC currency configuration for GiftBox
 * (GiftBox only supports USDC after removing cbBTC)
 * @returns {Object} USDC currency object with UI metadata
 */
export function getFixedCurrency() {
  const testnetOnly = import.meta.env.VITE_TESTNET_ONLY === 'true'
  return getCurrencyById('usdc', {
    includeLocal: false,
    includeMainnets: !testnetOnly,
    includeTestnets: testnetOnly,
    networkIds: [testnetOnly ? 'base_sepolia' : 'base']
  })
}

// Minimum deposit amounts per currency (for direct deposits)
// Used to show warnings to users about minimum amounts
export const CURRENCY_MIN_DEPOSITS = {
  'usdc': {
    amount: '10', // 10 USDC
    usd: 10
  },
  'cbbtc': {
    amount: '0.0002', // ~$10 worth of BTC
    usd: 10
  },
  'eth': {
    amount: '0.005', // ~$10 worth of ETH
    usd: 10
  },
  'usdt': {
    amount: '10', // 10 USDT
    usd: 10
  },
  // Default fallback
  'default': {
    amount: '10',
    usd: 10
  }
};

/**
 * Get minimum deposit amount for a currency
 * @param {string} currencyId - Currency ID (e.g., 'usdc', 'cbbtc')
 * @returns {{ amount: string, usd: number }}
 */
export function getMinDepositAmount(currencyId) {
  if (!currencyId) return CURRENCY_MIN_DEPOSITS.default;
  const id = currencyId.toLowerCase();
  return CURRENCY_MIN_DEPOSITS[id] || CURRENCY_MIN_DEPOSITS.default;
}
